import React, { useEffect, useState, useRef } from "react";
import ResponsiveDataManagement from "../../Components/Common/ResponsiveManagementLayout";
import SupplierService from "../../services/OrderingServices/SupplierService";
import PaymentTypeServices from "../../services/OrderingServices/PaymentTypeService";
import DeleteModal from "../../Components/Common/DeleteModal";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Card,
  CardBody,
  Row,
  Col,
  InputGroup,
  InputGroupText,
  Input,
} from "reactstrap";
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";

const formatLocalDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const Supplier = () => {
  const navigate = useNavigate();
  const purchaseIdDebounceRef = useRef(null);

  // ── list state ───────────────────────────────────────────────────────────────
  const [supplierList, setSupplierList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [totalRows, setTotalRows] = useState(0);

  // ── pagination state (server-side) ───────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // ── filter state ─────────────────────────────────────────────────────────────
  const [filters, setFilters] = useState({
    supplierId: null,
    paymentType: null,
    purchaseId: "",
    startDate: "",
    endDate: "",
  });

  // ── dropdown options ─────────────────────────────────────────────────────────
  const [supplierOptions, setSupplierOptions] = useState([]);
  const [paymentTypeOptions, setPaymentTypeOptions] = useState([]);

  // ── fetch dropdown data on mount ─────────────────────────────────────────────
  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const suppliers = await SupplierService.getList();
        const mapped = (
          Array.isArray(suppliers) ? suppliers : suppliers?.items || []
        ).map((s) => ({
          value: s.id,
          label:
            s.supplierName ||
            s.name ||
            `${s.firstName ?? ""} ${s.lastName ?? ""}`.trim(),
        }));
        setSupplierOptions(mapped);
      } catch (err) {
        console.error("Failed to load suppliers:", err);
      }

      try {
        const types = await PaymentTypeServices.getList();
        const mapped = (Array.isArray(types) ? types : []).map((t) => ({
          value: t.id?.toString() ?? t.name,
          label: t.name,
        }));
        setPaymentTypeOptions(mapped);
      } catch (err) {
        console.error("Failed to load payment types:", err);
      }
    };

    loadDropdowns();
  }, []);

  // ── core fetch (server-side filtered + paginated) ────────────────────────────
  const fetchSuppliers = async ({
    overrideFilters,
    overridePage,
    overridePageSize,
  } = {}) => {
    const active = overrideFilters ?? filters;
    const page = overridePage ?? currentPage;
    const size = overridePageSize ?? pageSize;

    try {
      setLoading(true);
      const response = await SupplierService.getPaymentList({
        supplierId: active.supplierId ?? undefined,
        paymentType: active.paymentType ?? undefined,
        purchaseId: active.purchaseId || undefined,
        startDate: active.startDate,
        endDate: active.endDate,
        page,
        pageSize: size,
      });

      // Support both flat array and paginated { items, totalCount } responses
      if (Array.isArray(response)) {
        setSupplierList(response);
        setTotalRows(response.length);
      } else {
        setSupplierList(response?.items || []);
        setTotalRows(
          response?.totalCount ?? response?.totalRows ?? response?.total ?? 0,
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Initial load — compute default date range first, then fetch with those dates
  useEffect(() => {
    const today = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(today.getMonth() - 1);

    const startDate = formatLocalDate(oneMonthAgo);
    const endDate = formatLocalDate(today);

    const initialFilters = { ...filters, startDate, endDate };
    setFilters(initialFilters);
    fetchSuppliers({ overrideFilters: initialFilters });
  }, []);
  // ── filter handlers (each resets to page 1) ───────────────────────────────────
  const handleSupplierChange = (option) => {
    const newFilters = { ...filters, supplierId: option ? option.value : null };
    setFilters(newFilters);
    setCurrentPage(1);
    fetchSuppliers({ overrideFilters: newFilters, overridePage: 1 });
  };

  const handlePaymentTypeChange = (option) => {
    const newFilters = {
      ...filters,
      paymentType: option ? option.value : null,
    };
    setFilters(newFilters);
    setCurrentPage(1);
    fetchSuppliers({ overrideFilters: newFilters, overridePage: 1 });
  };

  const handleDateRangeChange = (selectedDates) => {
    if (selectedDates.length === 2) {
      const [start, end] = selectedDates;
      const newFilters = {
        ...filters,
        startDate: formatLocalDate(start),
        endDate: formatLocalDate(end),
      };
      setFilters(newFilters);
      setCurrentPage(1);
      fetchSuppliers({ overrideFilters: newFilters, overridePage: 1 });
    }
  };

  // Debounced purchase-id text filter
  const handlePurchaseIdChange = (e) => {
    const val = e.target.value;
    const newFilters = { ...filters, purchaseId: val };
    setFilters(newFilters);
    clearTimeout(purchaseIdDebounceRef.current);
    purchaseIdDebounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchSuppliers({ overrideFilters: newFilters, overridePage: 1 });
    }, 400);
  };

  // ── pagination handler ────────────────────────────────────────────────────────
  const handlePaginationChange = ({ page, perPage }) => {
    setCurrentPage(page);
    setPageSize(perPage);
    fetchSuppliers({ overridePage: page, overridePageSize: perPage });
  };

  // ── table actions ─────────────────────────────────────────────────────────────
  const handleAdd = () => navigate("/suppliers/payment");

  const handleDelete = async () => {
    if (!selectedSupplier) return;
    try {
      await SupplierService.delete(selectedSupplier.id);
      setSupplierList((prev) =>
        prev.filter((item) => item.id !== selectedSupplier.id),
      );
      toast.success("Supplier payment deleted successfully");
    } catch (error) {
      toast.error("Failed to delete supplier payment");
    } finally {
      setDeleteModal(false);
      setSelectedSupplier(null);
    }
  };

  // ── columns ───────────────────────────────────────────────────────────────────
  const columns = [
    {
      name: "S.N",
      selector: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: "65px",
    },
    {
      name: "Supplier",
      selector: (row) => row.supplierName,
      sortable: true,
      cell: (row) => (
        <span
          style={{ cursor: "pointer", color: "#0d6efd" }}
          onClick={() => navigate(`/suppliers/detail/${row.supplierId}`)}
        >
          {row.supplierName}
        </span>
      ),
    },
    {
      name: "Purchase Ref",
      selector: (row) => row.purchaseReferenceNo,
      sortable: true,
    },
    {
      name: "Date",
      selector: (row) => row.paymentDate,
      cell: (row) => row.paymentDate_Nep,
      sortable: true,
    },
    {
      name: "Method",
      selector: (row) => row.paymentTypeName,
      sortable: true,
    },
    {
      name: "Amount",
      selector: (row) => row.paymentAmount,
      sortable: true,
      right: true,
    },

    { name: "Bank Name", selector: (row) => row.bankName, sortable: true },
    {
      name: "Cheque No",
      selector: (row) => row.chequeNo,
      sortable: true,
      right: true,
    },
    { name: "Remarks", selector: (row) => row.remarks, sortable: true },
  ];

  // ── filter bar (injected via customHeaderContent) ─────────────────────────────
  const filterBar = (
    <Row className="g-2 align-items-end mb-1">
      {/* Supplier */}
      <Col xs={12} md={3}>
        <label className="form-label mb-1 small fw-semibold">Supplier</label>
        <Select
          options={supplierOptions}
          value={
            supplierOptions.find((o) => o.value === filters.supplierId) || null
          }
          onChange={handleSupplierChange}
          placeholder="All Suppliers"
          isClearable
          menuPortalTarget={document.body}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
        />
      </Col>

      {/* Payment Type */}
      <Col xs={12} md={3}>
        <label className="form-label mb-1 small fw-semibold">
          Payment Method
        </label>
        <Select
          options={paymentTypeOptions}
          value={
            paymentTypeOptions.find((o) => o.value === filters.paymentType) ||
            null
          }
          onChange={handlePaymentTypeChange}
          placeholder="All Types"
          isClearable
          menuPortalTarget={document.body}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
        />
      </Col>

      {/* Purchase ID */}
      <Col xs={12} md={3}>
        <label className="form-label mb-1 small fw-semibold">
          Purchase Ref
        </label>
        <Input
          type="text"
          placeholder="Search purchase ref..."
          value={filters.purchaseId}
          onChange={handlePurchaseIdChange}
          style={{ height: "38px" }}
        />
      </Col>

      {/* Date Range */}
      <Col xs={12} md={3}>
        <label className="form-label mb-1 small fw-semibold">Date Range</label>
        <InputGroup>
          <Flatpickr
            className="form-control"
            placeholder="Select date range"
            value={[filters.startDate, filters.endDate]}
            onChange={handleDateRangeChange}
            options={{
              mode: "range",
              dateFormat: "Y-m-d",
              maxDate: "today",
              defaultDate: [filters.startDate, filters.endDate],
              allowInput: false,
            }}
          />
          <InputGroupText style={{ cursor: "pointer" }}>
            <i className="bx bx-calendar" />
          </InputGroupText>
        </InputGroup>
      </Col>
    </Row>
  );

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <ResponsiveDataManagement
        title="Supplier Payment"
        pageTitle="Supplier Management"
        mobileCardTitle="Supplier Payment"
        data={supplierList}
        columns={columns}
        searchKeys={[]} // search handled server-side
        showSearch={false} // hide built-in search box
        loading={loading}
        onAdd={handleAdd}
        totalRows={totalRows} // enables server-side pagination in the table
        currentPage={currentPage}
        pageSize={pageSize}
        onPaginationChange={handlePaginationChange}
        customHeaderContent={filterBar}
        mobileCardRenderer={(item) => (
          <Card className="shadow-sm rounded-3 border-0 border-start border-4 border-success mb-3">
            <CardBody className="p-3">
              <h6 className="fw-bold mb-1 text-primary">{item.supplierName}</h6>
              <p className="text-muted mb-1">
                <strong>Purchase Ref:</strong> {item.purchaseReferenceNo}
              </p>
              <p className="text-muted mb-1">
                <strong>Amount:</strong> {item.paymentAmount}
              </p>
              <p className="text-muted mb-1">
                <strong>Date:</strong> {item.paymentDate_Nep}
              </p>
              <p className="text-muted mb-1">
                <strong>Method:</strong> {item.paymentMethod}
              </p>
              <p className="text-muted mb-1">
                <strong>Bank:</strong> {item.bankName || "-"}
              </p>
              <p className="text-muted mb-1">
                <strong>Cheque No:</strong> {item.chequeNo || "-"}
              </p>
              <p className="text-muted mb-0">
                <strong>Remarks:</strong> {item.remarks || "-"}
              </p>
            </CardBody>
          </Card>
        )}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
        selectedItem={selectedSupplier}
      />
    </>
  );
};

export default Supplier;
